/**
 * ModQueuePanel — Founder/admin review of RAELA-flagged messages.
 *
 * Lists open items by priority, lets the reviewer dismiss (false positive)
 * or mark as actioned (warning sent / sender already auto-suspended).
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  useModQueue,
  type ModQueuePriority,
  type ModQueueStatus,
} from "@/lib/moderation/useModQueue";
import { ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";

const priorityVariant: Record<ModQueuePriority, "default" | "secondary" | "destructive" | "outline"> = {
  low: "outline",
  normal: "secondary",
  high: "default",
  critical: "destructive",
};

const QueueList = ({ status }: { status: ModQueueStatus }) => {
  const { items, loading, error, resolve } = useModQueue(status);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-8 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          {status === "open" ? "Queue is clear." : `No ${status} items.`}
        </p>
      </div>
    );
  }

  const onResolve = async (id: string, kind: "dismissed" | "actioned") => {
    setBusy(id);
    await resolve(id, kind, notes[id]?.trim() ?? "");
    setBusy(null);
  };

  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.id}>
          <Card className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={priorityVariant[it.priority]}>
                {it.priority === "critical" && <ShieldAlert className="mr-1 h-3 w-3" />}
                {it.priority}
              </Badge>
              <Badge variant="outline">{it.surface}</Badge>
              <Badge variant="outline">{it.severity}</Badge>
              <Badge variant="outline">{it.action}</Badge>
              <Badge variant="secondary">{it.strike_count} active strike{it.strike_count === 1 ? "" : "s"}</Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {new Date(it.created_at).toLocaleString()}
              </span>
            </div>

            {it.excerpt && (
              <blockquote className="border-l-2 border-border pl-3 text-sm italic text-foreground/80">
                "{it.excerpt}"
              </blockquote>
            )}

            <div className="text-xs font-mono text-muted-foreground space-y-0.5">
              <div>member: {it.member_id.slice(0, 8)}…</div>
              {it.rationale && <div>rationale: {it.rationale}</div>}
              {it.resolution_note && <div>resolution: {it.resolution_note}</div>}
            </div>

            {status === "open" && (
              <div className="space-y-2 pt-1">
                <Textarea
                  placeholder="Reviewer note (optional)…"
                  value={notes[it.id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))}
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onResolve(it.id, "dismissed")}
                    disabled={busy === it.id}
                  >
                    <ShieldX className="h-4 w-4" /> Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onResolve(it.id, "actioned")}
                    disabled={busy === it.id}
                  >
                    <ShieldCheck className="h-4 w-4" /> Mark actioned
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </li>
      ))}
    </ul>
  );
};

export const ModQueuePanel = () => (
  <div className="space-y-4">
    <div>
      <h3 className="text-base font-medium">RAELA moderation queue</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Every flagged DM, community message, and RAELA chat lands here. Items at
        critical priority indicate the sender has already been auto-suspended.
      </p>
    </div>
    <Tabs defaultValue="open" className="space-y-4">
      <TabsList>
        <TabsTrigger value="open">Open</TabsTrigger>
        <TabsTrigger value="actioned">Actioned</TabsTrigger>
        <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
      </TabsList>
      <TabsContent value="open"><QueueList status="open" /></TabsContent>
      <TabsContent value="actioned"><QueueList status="actioned" /></TabsContent>
      <TabsContent value="dismissed"><QueueList status="dismissed" /></TabsContent>
    </Tabs>
  </div>
);

export default ModQueuePanel;
